import { useEffect, useState } from 'react';

/**
 * A path-based router in forty lines.
 *
 * Path-based rather than hash-based because the OAuth redirect_uri cannot contain a fragment —
 * normalizeRedirectUris rejects it outright — and because CloudFront already rewrites extensionless
 * paths to index.html for exactly this.
 */

export function navigate(path: string): void {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function useRoute(): string {
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  return path;
}

export interface Route {
  name: 'list' | 'create' | 'detail' | 'callback' | 'signed-out' | 'not-found';
  identifier?: string;
}

export function parseRoute(path: string): Route {
  const parts = path.split('/').filter(Boolean);

  if (parts.length === 0) return { name: 'list' };
  if (parts[0] === 'auth' && parts[1] === 'callback') return { name: 'callback' };
  if (parts[0] === 'signed-out') return { name: 'signed-out' };
  if (parts[0] === 'apps') {
    if (parts[1] === 'new') return { name: 'create' };
    if (parts.length === 2) return { name: 'detail', identifier: decodeURIComponent(parts[1]) };
  }
  return { name: 'not-found' };
}
